import { useCallback, useState } from 'react';
import type { DatasetColumnHierarchy } from 'src/components/star-grid/datasets/datasetTypes';

export interface HierarchyChooser {
  /** The dimension whose "Choose Another Hierarchy" dialog is open, or null when none is. */
  readonly openFor: string | null;
  readonly open: (column: string) => void;
  readonly close: () => void;
  /** Hierarchies the user hand-picked for a column, kept so they stay selectable after switching away. */
  readonly pickedFor: (column: string) => readonly DatasetColumnHierarchy[];
  /** Records a hand-picked `(dimension, hierarchy)` pair against its column. */
  readonly remember: (column: string, pair: DatasetColumnHierarchy) => void;
}

const NONE: readonly DatasetColumnHierarchy[] = [];

/** Tracks which dimension's hierarchy chooser is open and the pairs it has picked, kept out of the step. */
export function useHierarchyChooser(): HierarchyChooser {
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [picked, setPicked] = useState<Record<string, DatasetColumnHierarchy[]>>({});
  const remember = useCallback((column: string, pair: DatasetColumnHierarchy) => {
    setPicked((prev) => {
      const existing = prev[column] ?? [];
      if (existing.some((entry) => entry.dimension === pair.dimension && entry.hierarchy === pair.hierarchy)) return prev;
      return { ...prev, [column]: [...existing, pair] };
    });
  }, []);
  return {
    openFor,
    open: useCallback((column: string) => setOpenFor(column), []),
    close: useCallback(() => setOpenFor(null), []),
    pickedFor: useCallback((column: string) => picked[column] ?? NONE, [picked]),
    remember
  };
}
